import Reflux from 'reflux';
import BaseMixin from 'flux/stores/BaseMixin';
import PDFExportFormActions from 'flux/actions/cuesheet.printable/PDFExportFormActions';
import EventViewActions from 'flux/actions/EventViewActions';
import TeamViewActions from 'flux/actions/TeamViewActions';
import FormMixin from 'flux/stores/FormMixin';
import EventViewMixin from 'flux/stores/EventViewMixin';
import ToastActions from 'flux/actions/ToastActions';
import { Map, OrderedMap, List, fromJS } from 'immutable';
import Rx from 'rxjs/Rx';
import _ from 'underscore';

import CuesheetStore from 'flux/stores/cuesheet/CuesheetStore';

import { Orientation, PageFormats, RowSizes } from 'shared/constants/Export';

import { EditableCellTypes } from 'flux/stores/cuesheet/CuesheetConstants';
import { DisplayTypes } from 'shared/constants/FlowConstants';

const documentCustomizationDefaults = {
  orientation: Orientation.landscape,
  rowSize: RowSizes.Compact,
  format: PageFormats.Letter,
  displayTeamTitle: true,
  displayTeamLogo: true,
  displayEventTitle: true,
  displayEventLogo: true,
  displayShowTitle: true,
  grayscale: false,
  includePrivateNoteContents: true,
};

const defaultConfig = Object.assign(
  {
    columnOrder: [],
    customPageBreaks: List(),
    downloading: false,
    transientPresetChange: false,
    loading: true,
    generatingPDFUrl: false,
    generatingGuestPDFUrl: false,
  },
  documentCustomizationDefaults,
);

/**
 * PDF Export Form Store
 *
 */
export default Reflux.createStore({
  mixins: [BaseMixin, FormMixin, EventViewMixin],

  listenables: [PDFExportFormActions],

  init() {
    this.setState(this._getInitState());
    this.listenTo(CuesheetStore, this.onCuesheetStoreChange);
    this.listenTo(EventViewActions.populate.failed, 'onEventPopulateFailed');
    this.listenTo(
      EventViewActions.populate.completed,
      'onEventPopulateCompleted',
    );
    this.listenTo(TeamViewActions.populate.failed, 'onTeamPopulateFailed');
    this.listenTo(
      TeamViewActions.populate.completed,
      'onTeamPopulateCompleted',
    );
    this._observables = {};
    this._observables.updatePDF = new Rx.Subject();
    this._observables.updatePDF.debounceTime(2000).subscribe(this._updatePDF);
  },

  _getInitState() {
    const state = this.getState();

    let next = Map(defaultConfig);

    if (state) {
      next = next.set('eventTitle', state.get('eventTitle'));
      next = next.set('eventLogoUrl', state.get('eventLogoUrl'));
      next = next.set('twentyFourHour', state.get('twentyFourHour'));
      next = next.set('timeZone', state.get('timeZone'));
      next = next.set('teamTitle', state.get('teamTitle'));
      next = next.set('teamLogoUrl', state.get('teamLogoUrl'));
    }

    return next;
  },

  onUpdateField(field, value) {
    FormMixin.onUpdateField.call(this, field, value);

    this._updatePDFDebounce();

    this.setInStateAndTrigger('transientPresetChange', true);
  },

  onGeneratePDF() {
    this.setInStateAndTrigger('generatingPDFUrl', true);
  },

  onGeneratePDFCompleted({ signedPDFUrl }) {
    this.setInStateAndTrigger('generatingPDFUrl', false);
    this.setInStateAndTrigger('pdfUrl', signedPDFUrl);
  },

  onGeneratePDFFailed() {
    let next = this.getState();
    next = next.set('generatingPDFUrl', false).set('pdfUrl', null);

    this.setInStateAndTrigger(next);

    ToastActions.failure(
      'PDF export failed - please reach out to support for assistance.',
      5000,
    );
  },

  onGenerateGuestPDF() {
    this.setInStateAndTrigger('generatingGuestPDFUrl', true);
  },

  onGenerateGuestPDFFailed() {
    let next = this.getState();
    next = next.set('generatingGuestPDFUrl', false).set('guestPDFUrl', null);

    this.setStateAndTrigger(next);

    ToastActions.failure(
      'PDF export failed - please reach out to support for assistance.',
      5000,
    );
  },

  onGenerateGuestPDFCompleted({ signedPDFUrl }) {
    this.setInStateAndTrigger('generatingGuestPDFUrl', false);
    this.setInStateAndTrigger('guestPDFUrl', signedPDFUrl);
  },

  _updatePDFDebounce() {
    if (this.getInState('loading')) return;

    this.deleteInStateAndTrigger('pdfUrl');

    this._observables.updatePDF.next(this._getCleanConfig());
  },

  _updatePDF(config) {
    PDFExportFormActions.generatePDF(config);
  },

  _getCleanConfig() {
    //The data for our pdf export (https://github.com/SHOFLO/api.v3/blob/dev/lambda/export/functions/pdf/index.js)
    //comes from a filtered set of the pdfExportFormStore's state. Using the same data from
    //the preview state helps our final rendered pdf match as closely as possible
    return this.getState()
      .delete('downloading')
      .delete('show')
      .delete('allColumns')
      .delete('cuesheetColumnOrder')
      .delete('cuesheetFullColumnOrder')
      .delete('cuesheetFullColumns')
      .delete('dataColumns')
      .delete('transientPresetChange')
      .toJS();
  },

  onClear() {
    this.setStateAndTrigger(this._getInitState());
  },

  onClearPreset() {
    let next = this.getState().delete('activePresetId').delete('allColumns');

    this.setState(next);

    const cuesheetFullColumnOrder = next.get('cuesheetFullColumnOrder');
    const cuesheetFullColumns = next.get('cuesheetFullColumns');
    const cuesheetColumnOrder = next.get('cuesheetColumnOrder');

    const specialColumns = this._getSpecialColumns(next.get('show'));
    const dataColumns = this._getDataColumns(
      cuesheetFullColumnOrder,
      cuesheetFullColumns,
      cuesheetColumnOrder,
    );

    const allColumns = specialColumns.merge(dataColumns);

    const columnOrder = this._getColumnOrder(allColumns);

    let fullColumnOrderWithSpecialColumns = List();
    allColumns.forEach((v, k) => {
      fullColumnOrderWithSpecialColumns =
        fullColumnOrderWithSpecialColumns.push(k);
    });

    next = next
      .merge({
        allColumns,
        dataColumns,
        columnOrder,
      })
      .merge(documentCustomizationDefaults);

    this.setStateAndTrigger(next);
  },

  onSubmit() {
    this._updatePDFDebounce();
  },

  onTogglePageBreak(rowIndex) {
    const currPageBreaks = this.getInState('customPageBreaks'),
      currIndex = currPageBreaks.indexOf(rowIndex);

    if (currIndex >= 0) {
      this.setInStateAndTrigger(
        'customPageBreaks',
        currPageBreaks.splice(currIndex, 1),
      );
    } else {
      this.setInStateAndTrigger(
        'customPageBreaks',
        currPageBreaks.push(rowIndex),
      );
    }

    this._updatePDFDebounce();
  },

  onUpdateColumnVisibility(columnId, enabled) {
    let next = this.getState();

    const allColumns = next
      .get('allColumns')
      .setIn([columnId, 'enabled'], enabled);

    const columnOrder = this._getColumnOrder(allColumns);

    next = next
      .set('allColumns', allColumns)
      .set('columnOrder', columnOrder)
      .set('transientPresetChange', true);

    this.setStateAndTrigger(next);

    this._updatePDFDebounce();
  },

  onSetPresetInState(presetId) {
    let next = this.getState();
    let columns = next.get('allColumns');

    columns = columns.map((col) => {
      col = col.set('enabled', false);
      return col;
    });

    const preset = next.getIn(['pdfPresets', presetId]);
    const presetColumnOrder = preset.get('columnOrder');

    presetColumnOrder.forEach((colId) => {
      columns = columns.setIn([colId, 'enabled'], true);
    });

    next = next
      .set('allColumns', columns)
      .set('columnOrder', presetColumnOrder)
      .set('activePresetId', presetId)
      .set('transientPresetChange', false);

    next = next.mergeDeep(preset);

    this.setStateAndTrigger(next);

    this._updatePDFDebounce();
  },

  onSubmitNewPreset(title) {
    const state = this.getState();
    const teamId = state.get('teamId');
    const eventId = state.get('eventId');
    const showId = state.get('showId');

    const preset = this._getPDFPresetFromState(state);

    preset.title = title;

    PDFExportFormActions.createNewPreset(teamId, eventId, showId, preset);
  },

  onUpdatePreset(presetId, title) {
    const state = this.getState();
    const teamId = state.get('teamId');
    const eventId = state.get('eventId');
    const showId = state.get('showId');

    const preset = this._getPDFPresetFromState(state);

    if (title) preset.title = title;

    PDFExportFormActions.updatePresetPersistent(
      teamId,
      eventId,
      showId,
      presetId,
      preset,
    );
  },

  _getPDFPresetFromState(state) {
    return _.pick(
      state.toJS(),
      'orientation',
      'rowSize',
      'includePrivateNoteContents',
      'grayscale',
      'displayEventLogo',
      'displayEventTitle',
      'displayShowTitle',
      'displayTeamLogo',
      'displayTeamTitle',
      'format',
      'columnOrder',
    );
  },

  onUpdatePresetPersistentCompleted(pdfPreset) {
    this._setNewOrUpdatedPresetInState(pdfPreset);
  },

  onCreateNewPresetCompleted(pdfPreset) {
    this._setNewOrUpdatedPresetInState(pdfPreset);
  },

  _setNewOrUpdatedPresetInState(pdfPreset) {
    pdfPreset = fromJS(pdfPreset);
    const pdfPresetId = pdfPreset.get('_id');

    let next = this.getState();
    next = next.setIn(['pdfPresets', pdfPresetId], pdfPreset);
    next = next
      .set('activePresetId', pdfPresetId)
      .set('transientPresetChange', false);

    this.setStateAndTrigger(next);
  },

  onCreateNewPresetFailed() {
    console.log('failed to create new preset');
  },

  onEventPopulateCompleted(event) {
    const { title, photo, twentyFourHourClock, timeZone } = event;

    const next = this.getState()
      .set('eventTitle', title)
      .set('eventLogoUrl', photo)
      .set('twentyFourHour', twentyFourHourClock)
      .set('timeZone', timeZone);

    this.setStateAndTrigger(next);
  },

  // eslint-disable-next-line no-unused-vars
  onEventPopulateFailed(err) {
    console.log('event population failed');
  },

  onTeamPopulateCompleted(team) {
    const { title, photo } = team;

    const next = this.getState()
      .set('teamTitle', title)
      .set('teamLogoUrl', photo);

    this.setStateAndTrigger(next);
  },

  // eslint-disable-next-line no-unused-vars
  onTeamPopulateFailed(err) {
    console.log('team population failed');
  },

  onCuesheetStoreChange(cuesheet) {
    let next = this.getState();
    const prevShowId = next.get('showId');
    const showId = cuesheet.get('showId');

    if (prevShowId && prevShowId !== showId && next.has('pdfUrl')) {
      next = next.delete('pdfUrl');
    }

    const cuesheetFullColumns = cuesheet.get('fullColumns'),
      show = cuesheet.get('show');

    if (cuesheetFullColumns && show) {
      next = next.set('userId', cuesheet.get('userId'));

      /*
      set column visibility
      */
      const cuesheetColumnOrder = cuesheet.get('columnOrder');
      const cuesheetFullColumnOrder = cuesheet.get('fullColumnOrder');

      const specialColumns = this._getSpecialColumns(show);
      const dataColumns = this._getDataColumns(
        cuesheetFullColumnOrder,
        cuesheetFullColumns,
        cuesheetColumnOrder,
      );

      const allColumns = specialColumns.merge(dataColumns);

      const columnOrder = this._getColumnOrder(allColumns);

      let fullColumnOrderWithSpecialColumns = List();
      allColumns.forEach((v, k) => {
        fullColumnOrderWithSpecialColumns =
          fullColumnOrderWithSpecialColumns.push(k);
      });

      const { hostname, origin } = window.location;

      let pdfPresets = OrderedMap();

      if (show.has('pdfPresets')) {
        show.get('pdfPresets').forEach((pdfPreset) => {
          pdfPresets = pdfPresets.set(pdfPreset.get('_id'), pdfPreset);
        });
      }

      //The data in this store is not only used to help render the pdf preview but is also passed
      //to the aws lambda pdf export (https://github.com/SHOFLO/api.v3/blob/dev/lambda/export/functions/pdf/index.js)
      //so they can match as closely as possible
      next = next.merge(
        Map({
          allColumns,
          dataColumns,
          cuesheetColumnOrder,
          cuesheetFullColumns,
          cuesheetFullColumnOrder,
          columnOrder,
          showTitle: show.get('title'),
          showId,
          teamId: cuesheet.get('teamId'),
          eventId: cuesheet.get('eventId'),
          hostname,
          origin,
          pdfPresets,
          show,
          settings: cuesheet.get('settings'),
          loading: false,
        }),
      );
    }

    this.setStateAndTrigger(next);
  },

  _getColumnOrder(columns) {
    let columnOrder = List();
    columns.forEach((col, id) => {
      if (col.get('enabled')) {
        columnOrder = columnOrder.push(id);
      }
    });

    return columnOrder;
  },

  _getDataColumns(
    cuesheetFullColumnOrder,
    cuesheetFullColumns,
    cuesheetColumnOrder,
  ) {
    let enabledColumns = OrderedMap();

    cuesheetColumnOrder.forEach((columnId) => {
      let column = cuesheetFullColumns.get(columnId);
      column = column.set('enabled', true);
      enabledColumns = enabledColumns.set(columnId, column);
    });

    let disabledColumns = OrderedMap();
    cuesheetFullColumnOrder.forEach((columnId) => {
      if (enabledColumns.has(columnId)) return;
      let column = cuesheetFullColumns.get(columnId);
      column = column.set('enabled', false);
      disabledColumns = disabledColumns.set(columnId, column);
    });

    return enabledColumns.concat(disabledColumns);
  },

  _getSpecialColumns(show) {
    const displayType = show.get('displayType');

    return OrderedMap().withMutations((columns) => {
      if (
        show.get('displayItemNumbers') ||
        show.get('displayType') === DisplayTypes.show
      ) {
        this._setSpecialColumnMutable(
          columns,
          EditableCellTypes.ItemNumber,
          'Item Numbers',
        );
      }

      if (
        displayType === DisplayTypes.schedule ||
        displayType === DisplayTypes.list
      ) {
        this._getTimingColumns(show, columns);
      }

      if (show.get('displayItemTitles')) {
        this._setSpecialColumnMutable(
          columns,
          EditableCellTypes.ItemTitle,
          'Items',
        );
      }

      if (show.get('displayItemDescriptions')) {
        this._setSpecialColumnMutable(
          columns,
          EditableCellTypes.ItemDescription,
          'Item Description',
        );
      }

      if (
        displayType === DisplayTypes.show ||
        displayType === DisplayTypes.globalElementContainer
      ) {
        this._getTimingColumns(show, columns);
      }

      this._setSpecialColumnMutable(
        columns,
        EditableCellTypes.ItemNote,
        'Private Notes',
      );
    });
  },

  _setSpecialColumnMutable(columns, columnId, columnTitle) {
    const currState = this.getInState(['allColumns', columnId, 'enabled']);

    columns.set(
      columnId,
      Map({
        _id: columnId,
        title: columnTitle,
        enabled: typeof currState !== 'undefined' ? currState : true,
      }),
    );
  },

  _getTimingColumns(show, columns) {
    if (show.get('displayStartTime')) {
      this._setSpecialColumnMutable(
        columns,
        EditableCellTypes.ItemStartTime,
        'Start Time',
      );
    }

    if (show.get('displayPresetDuration')) {
      this._setSpecialColumnMutable(
        columns,
        EditableCellTypes.ItemPresetDuration,
        'Preset Time',
      );
    }

    if (show.get('displayDuration')) {
      this._setSpecialColumnMutable(
        columns,
        EditableCellTypes.ItemDuration,
        'Duration',
      );
    }
  },
});
